import { useState, useEffect } from 'react';
import api from '../lib/api';

export const useCurrency = () => {
  const [currency, setCurrency] = useState('USD');
  const [symbol, setSymbol] = useState('$');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Check if user has a saved preference
        const savedCurrency = localStorage.getItem('preferred_currency');
        if (savedCurrency) {
          setCurrency(savedCurrency);
          const symbols = {
            USD: '$', GBP: '£', EUR: '€', INR: '₹', 
            AED: 'AED', JPY: '¥', AUD: 'A$', CAD: 'C$'
          };
          setSymbol(symbols[savedCurrency] || '$');
          setLoading(false);
          return;
        }

        // Detect from IP
        const response = await api.get('/currency/detect');
        setCurrency(response.data.currency);
        setSymbol(response.data.symbol);
        
        // Save preference
        localStorage.setItem('preferred_currency', response.data.currency);
      } catch (error) {
        console.error('Currency detection failed:', error);
        // Default to USD
        setCurrency('USD');
        setSymbol('$');
      } finally {
        setLoading(false);
      }
    };

    detectCurrency();
  }, []);

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('preferred_currency', newCurrency);
    
    const symbols = {
      USD: '$', GBP: '£', EUR: '€', INR: '₹', 
      AED: 'AED', JPY: '¥', AUD: 'A$', CAD: 'C$'
    };
    setSymbol(symbols[newCurrency] || '$');
  };

  const convertPrice = (price, fromCurrency = 'USD') => {
    const rates = {
      USD: 1.0,
      GBP: 0.79,
      EUR: 0.92,
      INR: 83.12,
      AED: 3.67,
      JPY: 149.50,
      AUD: 1.53,
      CAD: 1.36,
    };

    if (fromCurrency === currency) return price;

    // Convert to USD first
    const usdPrice = price / (rates[fromCurrency] || 1);
    
    // Then to target currency
    const converted = usdPrice * (rates[currency] || 1);
    
    return Math.round(converted * 100) / 100;
  };

  const formatPrice = (price, fromCurrency = 'USD') => {
    const converted = convertPrice(price, fromCurrency);
    
    // Format based on currency
    if (currency === 'JPY' || currency === 'INR') {
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    
    return `${symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return {
    currency,
    symbol,
    loading,
    changeCurrency,
    convertPrice,
    formatPrice
  };
};
